import { LoadingOutlined, PlusOutlined, FileOutlined } from '@ant-design/icons';
import { Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { useMemo, useState, useEffect } from 'react';
import { uploadResourcePositionImage } from '@/api/upload';
import LottiePreview from '../LottiePreview';
import styles from './styles.module.less';

interface UploadImageProps {
  value?: string;
  onChange?: (url: string) => void;
  width?: number;
  height?: number;
  maxSize?: number; // 最大文件大小，单位 MB
  uploadApi?: (file: File) => Promise<any>; // 自定义上传接口
  className?: string;
  style?: React.CSSProperties;
  showSizeHint?: boolean; // 是否显示尺寸提示
  acceptWebp?: boolean; // 是否支持 webp 格式
  onlyWebp?: boolean;
  picDesc?: String;
  isLimitHeight?: boolean; // 是否限制宽高
  showResolution?: boolean; // 是否显示图片分辨率
  fileTypes?: string[]; // 允许的文件类型
  allowLottieSvga?: boolean; // 是否允许 Lottie/SVGA（默认关闭，避免全局外溢）
}

const UploadImage: React.FC<UploadImageProps> = ({
  value,
  onChange,
  width = 528,
  height = 264,
  maxSize = 20,
  uploadApi = uploadResourcePositionImage,
  className,
  style,
  showSizeHint = true,
  acceptWebp = false,
  onlyWebp = false,
  picDesc = '',
  isLimitHeight = false,
  showResolution = false,
  fileTypes,
  allowLottieSvga = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [imageResolution, setImageResolution] = useState<{ width: number; height: number } | null>(null);

  const allowedTypes = useMemo(() => {
    // 如果传入了自定义文件类型，优先使用
    let baseTypes: string[];
    if (fileTypes && fileTypes.length > 0) {
      baseTypes = fileTypes;
    } else if (onlyWebp) {
      baseTypes = ['image/webp'];
    } else {
      baseTypes = ['image/jpeg', 'image/png'];
      if (acceptWebp || picDesc) {
        baseTypes.push('image/webp');
      }
    }

    if (!allowLottieSvga) {
      return baseTypes;
    }

    // 允许 Lottie/SVGA 时，额外加入扩展名与常见 MIME，避免选择器置灰
    const extra = ['.json', '.lottie', '.svga', 'application/json', 'application/octet-stream'];
    const result = [...baseTypes, ...extra];
    return result;
  }, [acceptWebp, onlyWebp, picDesc, fileTypes, allowLottieSvga])

  // 获取图片分辨率的函数
  const getImageResolution = (imageUrl: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        reject(new Error('无法加载图片'));
        URL.revokeObjectURL(img.src);
      };
      img.src = imageUrl;
    });
  };

  // 当 value 改变时，如果启用了显示分辨率，获取现有图片的分辨率
  useEffect(() => {
    if (value && showResolution) {
      getImageResolution(value)
        .then((resolution) => {
          setImageResolution(resolution);
        })
        .catch((error) => {
          console.warn('获取现有图片分辨率失败:', error);
          setImageResolution(null);
        });
    } else if (!value) {
      setImageResolution(null);
    }
  }, [value, showResolution]);

  const handleUpload: UploadProps['beforeUpload'] = async (file) => {
    const fileNameLower = (file.name || '').toLowerCase();
    // 基本类型识别：图片 / Lottie / SVGA（仅在允许时）
    const isImage = (file.type || '').startsWith('image/');
    const isLottie = allowLottieSvga && (fileNameLower.endsWith('.json') || fileNameLower.endsWith('.lottie') || file.type === 'application/json');
    const isSvga = allowLottieSvga && (fileNameLower.endsWith('.svga') || file.type === 'application/octet-stream');
    const isValidFile = isImage || isLottie || isSvga;

    if (!isValidFile) {
      messageApi.error('只能上传图片、SVGA 或 Lottie 文件！');
      return false;
    }

    // picDesc 限制仅对图片生效（不限制 Lottie/SVGA）
    if (picDesc && isImage && file.type !== 'image/webp') {
      messageApi.error('Please upload a WebP image under 1 MB..');
      return false;
    }

    // 对图片类型再校验是否在允许列表中（非图片类型跳过）
    if (isImage && !allowedTypes.includes(file.type)) {
      const typeNames = allowedTypes.map(type => {
        switch (type) {
          case 'image/jpeg':
          case 'image/jpg':
            return 'JPG';
          case 'image/png':
            return 'PNG';
          case 'image/gif':
            return 'GIF';
          case 'image/webp':
            return 'WebP';
          default:
            return type.split('/')[1]?.toUpperCase() || type;
        }
      }).join('、');
      messageApi.error(`只能上传 ${typeNames} 格式的图片！`);
      return false;
    }

    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      messageApi.error(`图片大小不能超过 ${maxSize}MB！`);
      return false;
    }

    // 如果启用尺寸限制，先验证图片比例
    if (isLimitHeight) {
      try {
        await new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            // 计算期望的宽高比
            const expectedRatio = width / height;
            const actualRatio = img.width / img.height;
            
            // 允许一定的误差范围（0.01 约等于 1% 的误差）
            const tolerance = 0.02;
            
            if (Math.abs(actualRatio - expectedRatio) > tolerance) {
              messageApi.error(
                `图片比例不符合要求！期望比例 ${width}:${height} (${expectedRatio.toFixed(2)})，当前比例 ${img.width}:${img.height} (${actualRatio.toFixed(2)})`
              );
              reject(new Error('图片比例不符合要求'));
            } else {
              resolve();
            }
            // 释放内存
            URL.revokeObjectURL(img.src);
          };

          img.onerror = () => {
            messageApi.error('图片加载失败，请检查文件格式');
            reject(new Error('图片加载失败'));
          };

          img.src = URL.createObjectURL(file);
        });
      } catch (error) {
        return false; // 比例验证失败，阻止上传
      }
    }

    try {
      setLoading(true);
      const result = await uploadApi(file);
      console.log('上传结果:', result); // 调试信息
      if (result.status === 200 && result.data) {
        console.log('上传成功，图片URL:', result.data); // 调试信息
        onChange?.(result.data);
        messageApi.success('上传成功');
        
        // 如果启用了显示分辨率，获取图片分辨率
        if (showResolution) {
          try {
            const resolution = await getImageResolution(result.data);
            setImageResolution(resolution);
          } catch (error) {
            console.warn('获取图片分辨率失败:', error);
          }
        }
      } else {
        console.error('上传失败:', result); // 调试信息
        messageApi.error(result.message || '上传失败');
      }
    } catch (error) {
      console.error('上传异常:', error); // 调试信息
      messageApi.error('上传失败');
    } finally {
      setLoading(false);
    }

    return false;
  };

  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>点击上传</div>
     {picDesc && <div style={{fontSize: 11}}>{picDesc}</div>}
      {showSizeHint && (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: '#666',
            lineHeight: '16px',
          }}
        >
          建议尺寸：{width}x{height}
        </div>
      )}
    </div>
  );

  return (
    <>
      {contextHolder}
      <Upload
        name="file"
        listType="picture-card"
        accept={allowedTypes.join(",")}
        showUploadList={false}
        beforeUpload={handleUpload}
        className={`${styles.uploadImage} ${className || ''}`}
        style={
          {
            ...style,
            '--upload-width': `${width}px`,
            '--upload-height': `${height}px`,
          } as React.CSSProperties
        }
      >
        {value ? (
          <div className={styles.picContent}>
            {value.toLowerCase().endsWith('.svga') ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f5f5f5',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9',
                }}
              >
                <FileOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 8 }} />
                <div style={{ fontSize: 12, color: '#666' }}>SVGA 文件</div>
              </div>
            ) : (value.toLowerCase().endsWith('.json') || value.toLowerCase().endsWith('.lottie')) ? (
              // Lottie 文件显示
              <LottiePreview
                src={value}
                width="100%"
                height="100%"
                style={{
                  background: '#f5f5f5',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9',
                }}
              />
            ) : (
              // 图片文件显示
              <>
                <img
                  src={value}
                  alt="上传图片"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onLoad={() => console.log('图片加载成功:', value)}
                  onError={(e) => console.error('图片加载失败:', value, e)}
                />
                {showResolution && imageResolution && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      lineHeight: '16px',
                    }}
                  >
                    {imageResolution.width} × {imageResolution.height}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          uploadButton
        )}
      </Upload>
    </>
  );
};

export default UploadImage;
